import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MenuIcon, SearchIcon, HeartIcon, BellIcon, PlusCircleIcon, XIcon, ShoppingCartIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useBrand } from '../../hooks/useBrand';
import PlatformToggle from '../UI/PlatformToggle';
interface MobileTopBarProps {
  isReader: boolean;
  showTopBar: boolean;
  isMenuOpen: boolean;
  setIsMenuOpen: (isOpen: boolean) => void;
  isSearchOpen: boolean;
  setIsSearchOpen: (isOpen: boolean) => void;
  cartItemCount: number;
}
const MobileTopBar: React.FC<MobileTopBarProps> = ({
  isReader,
  showTopBar,
  isMenuOpen,
  setIsMenuOpen,
  isSearchOpen,
  setIsSearchOpen,
  cartItemCount
}) => {
  const dk = useDarkClasses();
  const {
    theme
  } = useBrand();
  const location = useLocation();
  const navigate = useNavigate();
  const isOnNotifications = location.pathname === '/notifications';
  const roleAccent = theme.roleAccent[isReader ? 'reader' : 'vendor'];
  const icon = dk.chromeIcon;
  const titleColor = theme.accent.chromeTitle ?? (dk.isDark ? 'text-white' : '');
  return <header className={`fixed top-0 left-0 right-0 backdrop-blur-sm shadow-sm z-40 transition-transform duration-300 md:hidden pt-safe ${showTopBar ? 'transform-none' : 'transform -translate-y-full'} ${dk.navTranslucent}`} data-id="element-2688" data-mp="2vu5hsw3d42bx">
      <div className={`flex items-center justify-between h-16 px-4 ${theme.chromeBar}`} data-id="element-2689" data-mp="3jkrcllocvq3z">
        {/* Left: menu or close */}
        <div className="flex items-center" data-id="element-2690" data-mp="3fkqzc4cztkyi">
          {isMenuOpen || isSearchOpen ? <button onClick={() => {
          setIsMenuOpen(false);
          setIsSearchOpen(false);
        }} className="p-2 -ml-2 touch-target flex items-center justify-center active:scale-95 transition-transform" aria-label="Close" data-id="element-2691" data-mp="3aj9zmc68w4ji">
              <XIcon className={`h-6 w-6 ${icon}`} data-id="element-2692" data-mp="1u5bh393cu2wk" />
            </button> : <button onClick={() => setIsMenuOpen(true)} className="p-2 -ml-2 touch-target flex items-center justify-center active:scale-95 transition-transform" aria-label="Menu" data-id="element-2693" data-mp="1vobhu4m7fvkd">
              <MenuIcon className={`h-6 w-6 ${icon}`} data-id="element-2694" data-mp="1moodpb23cbnm" />
            </button>}
        </div>

        {/* Center: platform toggle or search title */}
        <div className="flex-1 flex justify-center" data-id="element-2695" data-mp="1cvpobqyrsh2y">
          {isSearchOpen ? <span className={`text-lg font-semibold ${titleColor}`} data-id="element-2696" data-mp="1ho0p0w1c46wk">
              Search
            </span> : <PlatformToggle data-id="element-2697" data-mp="13xdocyc0k6xu" />}
        </div>

        {/* Right: context icons */}
        <div className="flex items-center space-x-2" data-id="element-2698" data-mp="17ypgbaid1497">
          {!isSearchOpen && <>
              <button onClick={() => setIsSearchOpen(true)} className="p-2 touch-target flex items-center justify-center active:scale-95 transition-transform" aria-label="Search" data-id="element-2699" data-mp="tyxzz34dfbs1">
                <SearchIcon className={`h-5 w-5 ${icon}`} data-id="element-2700" data-mp="fd0u46k5om9a" />
              </button>
              <button onClick={() => {
            if (isOnNotifications) {
              // Go back if there's history, otherwise home
              if (window.history.length > 2) {
                navigate(-1);
              } else {
                navigate('/');
              }
            } else {
              navigate('/notifications');
            }
          }} className="p-2 relative touch-target flex items-center justify-center active:scale-95 transition-transform" aria-label={isOnNotifications ? 'Close notifications' : 'Notifications'} data-id="element-2701" data-mp="adxnwisfw9zq">
                {isOnNotifications ? <XIcon className={`h-6 w-6 ${roleAccent.icon}`} data-id="element-2702" data-mp="1czr828s1nuo2" /> : <>
                    <BellIcon className={`h-5 w-5 ${icon}`} data-id="element-2703" data-mp="36gvsw65q91a6" />
                    <span className={`absolute top-1 right-1 ${roleAccent.countBadge} text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center`} data-id="element-2704" data-mp="3bgi82l548rlm">
                      3
                    </span>
                  </>}
              </button>
              {isReader ? <Link to="/wishlist" aria-label="Wishlist" className="p-2 relative touch-target flex items-center justify-center active:scale-95 transition-transform" data-id="element-2705" data-mp="2nk4adyy0eq76">
                  <HeartIcon className={`h-5 w-5 ${icon}`} data-id="element-2706" data-mp="31ilxbug3byo1" />
                </Link> : <Link to="/vendors/manage" aria-label="Create listing" className="p-2 touch-target flex items-center justify-center active:scale-95 transition-transform" data-id="element-2707" data-mp="cqrqso8i1qbc">
                  <PlusCircleIcon className={`h-5 w-5 ${theme.secondary.text}`} data-id="element-2708" data-mp="219cio1968kvl" />
                </Link>}
              <Link to="/cart" aria-label="Cart" className="p-2 relative touch-target flex items-center justify-center active:scale-95 transition-transform" data-id="element-2709" data-mp="1x9bz6277tjho">
                <ShoppingCartIcon className={`h-5 w-5 ${icon}`} data-id="element-2710" data-mp="1nh6bh4nzxo8a" />
                {cartItemCount > 0 && <span className={`absolute top-1 right-1 ${theme.roleAccent.vendor.countBadge} text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center`} data-id="element-2711" data-mp="1dkt79cutrrza">
                    {cartItemCount}
                  </span>}
              </Link>
            </>}
        </div>
      </div>
    </header>;
};
export default MobileTopBar;